import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ElasticsearchRadioGroup from '@client/components/ui/select/ElasticsearchRadioGroup';
import CultureTreeSelect from '@client/components/ui/select/CultureTreeSelect';

const CULTURE_FIELD = 'treatment_cultures.culture';
// SP-430 R17: Rapide must only show cultures flagged "à afficher dans le filtre
// de recherche Traitement rapide". CultureTreeSelect ignores the `endpoint` prop
// and filters via `filterParams` (same mechanism Multichoix uses). Stable module
// reference so it doesn't retrigger the tree fetch on every render.
const RAPIDE_CULTURE_FILTER = {
  show_in_customer_filter_quick_processing: true,
  valid_treatments_only: true,
};
const ANIMATION_CONFIG = {
  initial: false,
  animate: { height: 'auto', opacity: 1 },
  animateHidden: { height: 0, opacity: 0 },
  transition: { duration: 0.3 },
};

const StepContent = ({
  step,
  isActive,
  value,
  activeFacets,
  isValid,
  isRequired,
  isLastVisibleStep,
  onChange,
  onNext,
  onSkip,
  onForceRevalidation,
  onAutoSearch,
  selectionLabels,
}) => {
  const { t } = useTranslation('treatment-search');

  const hasSelections = value?.length > 0;
  const canProceed = isValid && !isLastVisibleStep;
  const isCultureStep = step.field === CULTURE_FIELD;

  const handleCultureChange = (selectedValues, selectedNodes) => {
    const values = Array.isArray(selectedValues) ? selectedValues : [];
    const nodes = Array.isArray(selectedNodes) ? selectedNodes : [];

    const labelObjects = nodes.filter(Boolean).map((node) => ({
      value: node.value || node.id,
      label: node.label || node.french_name || node.name || String(node.value || node.id),
      is_pfi: node.is_pfi || false,
      name_to_show_on_icon: node.name_to_show_on_icon || node.name_to_display_in_the_icon || null,
    }));

    onChange(step.key, values, labelObjects);

    if (isLastVisibleStep && values.length > 0) {
      // SP-430: selecting the last criterion auto-launches the search. Fired
      // synchronously inside the click gesture so window.open (new tab) is not
      // blocked by the browser.
      onAutoSearch?.(step.key, values, labelObjects);
    } else if (step.autoProgress && values.length > 0 && !isLastVisibleStep) {
      setTimeout(() => onNext(step.key + 1), 100);
    }
  };

  const handleElasticsearchChange = (selectedValues, selectedItems) => {
    const values = Array.isArray(selectedValues) ? selectedValues : [selectedValues].filter(Boolean);
    const labelObjects = Array.isArray(selectedItems) ? selectedItems : [selectedItems].filter(Boolean);

    onChange(step.key, values, labelObjects);

    if (isLastVisibleStep && values.length > 0) {
      // SP-430: auto-launch on last criterion (see handleCultureChange).
      onAutoSearch?.(step.key, values, labelObjects);
    } else if (step.autoProgress && values.length > 0 && !isLastVisibleStep) {
      setTimeout(() => onNext(step.key + 1), 100);
    }
  };

  const renderCultureSelect = () => (
    <CultureTreeSelect
      filterParams={RAPIDE_CULTURE_FILTER}
      labelField="french_name"
      isMulti={false}
      label={step.label ? t(step.label) : t(step.placeholder)}
      placeholder={t(step.placeholder)}
      value={value}
      onChange={handleCultureChange}
      checkStrictly
      languageFields={step?.languageConfig?.fields}
      languageLabels={step?.languageConfig?.labels}
      defaultLanguage={step?.languageConfig?.defaultLanguage || 'fr'}
      showLanguageSelector={!!step?.languageConfig}
      onLanguageChange={onForceRevalidation}
      style={{ width: '100%' }}
    />
  );

  // SP-430 R51/R55/R60/R64: criteria 2-5 are radio buttons (single choice,
  // alphabetical), not a search dropdown.
  const renderElasticsearchSelect = () => (
    <ElasticsearchRadioGroup
      document={step.document}
      field={step.field}
      valueField={step.value_field}
      labelField={step.label_field}
      value={value}
      onChange={handleElasticsearchChange}
      activeFacets={activeFacets}
      sortSourceField={step.sortSourceField}
      enabled={isActive}
    />
  );

  const renderActionButtons = () => (
    <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0">
      {hasSelections && canProceed && (
        <button
          onClick={() => onNext(step.key + 1)}
          className="w-full sm:w-auto text-sm text-green-700 hover:text-green-800 font-medium py-2 sm:py-0 px-3 sm:px-0 rounded sm:rounded-none bg-green-50 sm:bg-transparent hover:bg-green-100 sm:hover:bg-transparent text-center order-1"
        >
          Continuer vers l'étape {step.key + 1} →
        </button>
      )}
      
      {!isRequired && canProceed && (
        <button
          onClick={() => onSkip(step.key)}
          className="w-full sm:w-auto text-sm text-gray-600 hover:text-gray-800 font-medium py-2 sm:py-0 px-3 sm:px-0 rounded sm:rounded-none bg-gray-50 sm:bg-transparent hover:bg-gray-100 sm:hover:bg-transparent text-center order-2 sm:ml-auto"
        >
          Passer cette étape →
        </button>
      )}
    </div>
  );

  return (
    <motion.div
      {...ANIMATION_CONFIG}
      animate={
        isActive ? ANIMATION_CONFIG.animate : ANIMATION_CONFIG.animateHidden
      }
      className="overflow-hidden"
    >
      <div
        className="p-4 sm:p-6 bg-[#f8fdec] rounded-b-[16px]"
        style={{ display: isActive ? 'block' : 'none' }}
      >
        <div>
          {step.description && (
            <p className="text-gray-600 mb-4">{t(step.description)}</p>
          )}
          {isCultureStep ? renderCultureSelect() : renderElasticsearchSelect()}
          {renderActionButtons()}
        </div>
      </div>
    </motion.div>
  );
};

export default StepContent;
